// Light: what the player can see, and nothing else.
//
// **Visibility only, deliberately and completely.** Nothing in `sim/` calls
// anything in this file. A dark tile can be walked onto, cast at, pushed through
// and stood on exactly as a lit one can; `canObjectEnter` is unchanged, no
// settle system reads this, and every recorded take and every analysis search
// replays identically in a pitch-dark room and a bright one. Darkness is an
// *information* problem, not a second kind of wall. That is a design decision
// (2026-08-16) rather than a stage on the way to something: making light block
// movement would put a rule in front of the player that they cannot see, which
// is the opposite of what the Puzzle Charter's Rule 4 asks for.
//
// So this is a **query**, in the shape `traceWaterFlow` and `chargeNetwork`
// already have: a pure function of a room, recomputed from scratch by whoever
// draws it. It lives in `sim/` rather than in the renderer for the reason every
// other query does -- the World Editor draws the same answer, and two
// implementations of "what is lit" would be two answers.
//
// **Light is not a rune, and must never become one.** `03_Rune_Language.md`
// fixed the concept set on 2026-07-24 and named Light explicitly among the
// quantities that are *derived* rather than primitive. So there is no Light
// concept, no `light` cluster, and no authored "this glows" flag: what emits is
// **derived from what an object already is**, which is the whole of `lightStrength`
// below and the only part of this file worth arguing about.
import { ObjectDef, RoomDef } from './types';
import { PhysicsConfig, DEFAULT_PHYSICS } from './physics';
import { Caster, CasterTile, isConduitCharged, networkPotential, pathBlocked, solidTiles } from './queries';

/** The top of the room-light scale: fully lit, and the default. */
export const FULLY_LIT = 10;

/** How much light this room eats, `0` for an ordinary room.
 *
 * The room's own number is stored as *light* (1 dark, 10 lit) because that is
 * how an author thinks about a room, and used as *darkness* because that is how
 * the physics reads -- a quantity a source has to overcome. One field, converted
 * in one place, rather than two fields that could disagree. Same shape
 * `doorLocks` uses in storing *is locked* and reading `=== false` for open. */
export function roomDarkness(room: RoomDef): number {
  return Math.max(0, FULLY_LIT - (room.light ?? FULLY_LIT));
}

/** Whether this room draws any darkness at all. The renderer's early-out, and
 * the honest reading of "every room the world shipped with". */
export function roomIsDark(room: RoomDef): boolean {
  return roomDarkness(room) > 0;
}

/** How brightly this object is shining right now, `0` for the overwhelming
 * majority that are not shining at all.
 *
 * **Derived, never authored, and this is the whole design.** The world already
 * contains Fireplaces, Braziers, Candles, a Bonfire on a `heatState` ladder,
 * Lava at Energy 30, molten glass, Charged Fonts, Electric Lamps and four altar
 * pillars that visibly light up. If light came from a cluster somebody had to
 * remember to add, the first object to be forgotten would be a thing that
 * plainly glows sitting in the dark -- which is `CLAUDE.md`'s single
 * most-repeated bug, one more time.
 *
 * The tempting rule is "hot things glow", and the authored world rules it out in
 * one row: **a Riverbed holds Energy 8** (authored above its own freeze
 * threshold so it stays liquid), which is hotter than a lit Brazier's own
 * ceiling would suggest and is not on fire. Warmth is not emission. What
 * separates the two is that a light source is a *source* -- something holding
 * itself above its surroundings, or converting something else -- so each branch
 * below names a way the world already says exactly that:
 *
 * | Branch | Reads | Gets right |
 * | --- | --- | --- |
 * | a fire | a `heatState` stage that declares itself a source (`holds`/`bias`) | lit Brazier 16, lit Candle 2, lit Bonfire ambient+3; every unlit one 0 |
 * | a reservoir | `maxProperties.energy`, an object that regenerates toward a ceiling | Fireplace, Brazier, Candle -- and embers, since a fire just gone out fades as it cools rather than snapping dark |
 * | a radiator | `thermalContact` on Energy, a thing that pushes heat into what touches it | Lava 30, Heated Plate 12, Scorched Floor 10, molten glass -- and Cold Slab **0**, which falls out rather than being excluded |
 * | a circuit | a charged `conduit.feed`'s network potential, or a store's | Electric Lamp, Gate Terminal, Conveyor Console, Gate Pillar, Charged Font |
 * | a lit fixture | it is *showing* its `litTexture` while holding nothing | the four altar pillars |
 *
 * and everything else -- a Riverbed, an Ice Block, a Bush, a Sealed Reliquary, a
 * Metal Floor, a length of Cabling -- reads 0 without needing to be named.
 *
 * The circuit branch is the one with teeth. A lamp's strength is the **network
 * potential**, not a constant, so wiring a second consumer onto its line drops
 * the line from 4 to 2.67 and the lamp goes out -- and pouring Charge onto the
 * line pushes the potential up and the light *further*, until relaxation pulls
 * it back. That is the capacity model made spatial, and it is most of the reason
 * this feature is worth having. Gated on `isConduitCharged` so that what a
 * consumer emits and what it draws itself as can never disagree: a lamp below
 * its threshold is dark on both counts. */
export function lightStrength(room: RoomDef, obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS, caster?: Caster): number {
  const energy = obj.properties?.energy ?? 0;
  let strength = 0;

  // A fire, in a stage that declares itself a source rather than a reservoir --
  // `holds` above zero (a hearth at a fixed level) or `bias` above zero (a
  // bonfire that sits above whatever room it is in). An unlit stage declares
  // neither and so emits nothing, which is what "unlit" should mean.
  const stage = obj.heatState?.concept === 'energy' ? obj.heatState.states[obj.heatState.current] : undefined;
  if (stage && ((stage.holds ?? 0) > 0 || (stage.bias ?? 0) > 0)) strength = Math.max(strength, energy);
  // An active reservoir: something that regenerates toward its own ceiling
  // rather than merely happening to be warm. Overlaps the branch above on every
  // hearth in the world, harmlessly, and covers the one case it does not: a fire
  // that has just gone out is still hot, and glows as embers until it cools.
  if ((obj.maxProperties?.energy ?? 0) > 0) strength = Math.max(strength, energy);
  // A radiator: it pushes heat into whatever touches it, so it is radiating.
  // This is the branch that makes severity a consequence rather than a constant
  // -- Lava, a Heated Plate and a Cold Slab carry the identical cluster and
  // differ only in how hot they are, so the cold one emits nothing with no
  // special case for it.
  if (obj.thermalContact?.concept === 'energy') strength = Math.max(strength, energy);
  // A circuit. A consumer emits exactly while it reads charged -- the same call
  // that decides whether it draws itself lit -- and a store with no feed of its
  // own (a Charged Font) emits what it is holding. A capacity-0 conductor emits
  // nothing, which is what keeps a run of Cabling from becoming a light strip.
  if (obj.conduit?.feed) {
    if (isConduitCharged(room, obj, physics, caster)) strength = Math.max(strength, networkPotential(room, obj, physics, caster));
  } else if ((obj.capacity ?? 0) > 0) {
    strength = Math.max(strength, networkPotential(room, obj, physics, caster));
  }
  // A fixture that is *showing* its lit look while holding neither heat nor
  // charge: an altar pillar, whose lit state is a boolean with no quantity
  // behind it. Read off the texture because that is where the state actually
  // lives -- `applyInteract` writes `litTexture` back onto a filled altar (a
  // conduit feed's lit look is only announced, never written, so this branch
  // cannot double-count one).
  if (obj.litTexture !== undefined && obj.texture === obj.litTexture) strength = Math.max(strength, physics.light.litFixture);

  return strength;
}

/** How far this object's light actually carries, in tiles.
 *
 * `strength * tilesPerUnit - darkness`, floored at 0 and capped at
 * `maxReach`. A source with any strength at all lights **at least its own
 * tile**, which is what a candle in a deep cellar should do; one with no
 * strength lights nothing, including its own tile, because it is not a light. */
export function lightReach(room: RoomDef, obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS, caster?: Caster): number {
  const strength = lightStrength(room, obj, physics, caster);
  if (strength <= 0) return -1;
  const reach = strength * physics.light.tilesPerUnit - roomDarkness(room);
  return Math.max(0, Math.min(physics.light.maxReach, reach));
}

/** Every tile the caster can currently see, as `"x,y"` keys.
 *
 * A **circle**, not a grid metric: `dx² + dy² <= reach²`. A diamond or a square
 * would be more countable, which is the argument the Archer's `standoff` wins on
 * -- but nobody plans around the exact tile a lamp stops at the way they plan a
 * mob's approach, and a round pool of light with shadows cut out of it is what
 * the thing being modelled looks like.
 *
 * **Walls and solid objects cast shadows**, through `queries.pathBlocked` --
 * the same walk a `Project` beam takes, which is what stops light and a beam
 * from disagreeing about what a wall is. It also means anything that stops a
 * beam stops light, so **a pushed Boulder is a movable shadow**: that falls out
 * of reusing the predicate rather than being built.
 *
 * The caster always sees `physics.light.casterReach` tiles around themselves
 * regardless of the room -- see that field for why it is not optional.
 *
 * Returns every in-bounds tile for a room that is not dark, so a caller that
 * skipped `roomIsDark` still gets a correct answer rather than a wrong one; the
 * renderer checks first only to avoid the work. */
export function litTiles(room: RoomDef, caster?: CasterTile & Caster, physics: PhysicsConfig = DEFAULT_PHYSICS): Set<string> {
  const lit = new Set<string>();
  const inBounds = (x: number, y: number) => x >= 0 && y >= 0 && x < room.cols && y < room.rows;

  if (!roomIsDark(room)) {
    for (let y = 0; y < room.rows; y++) for (let x = 0; x < room.cols; x++) lit.add(`${x},${y}`);
    return lit;
  }

  // One pass over the room's blockers, shared by every ray below: room16 has 173
  // objects, and asking each of them at every step of every ray is the
  // difference between this being free and being the most expensive thing the
  // renderer does.
  const solid = solidTiles(room);
  const shine = (fromX: number, fromY: number, reach: number) => {
    const r = Math.floor(reach);
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx * dx + dy * dy > reach * reach) continue;
        const x = fromX + dx;
        const y = fromY + dy;
        if (!inBounds(x, y) || lit.has(`${x},${y}`)) continue;
        // A wall tile is lit if the light reaches it -- you can see the wall the
        // lamp is shining on, and a room whose edges were dark would read as a
        // void rather than as a chamber.
        if (pathBlocked(room, fromX, fromY, x, y, solid)) continue;
        lit.add(`${x},${y}`);
      }
    }
  };

  for (const obj of room.objects) {
    const reach = lightReach(room, obj, physics, caster);
    if (reach >= 0) shine(obj.x, obj.y, reach);
  }
  if (caster) shine(caster.x, caster.y, physics.light.casterReach);
  return lit;
}

/** Whether a room could ever be seen in: it is dark, and nothing in it emits.
 *
 * Read by `worldIssues` rather than by the loader, and a warning rather than an
 * error, for the reason every authoring check here is: laying out a dark room
 * before deciding where its light comes from is a normal order to work in. What
 * it catches is the finished version of that -- a room nobody can ever see,
 * which is a very expensive thing to discover by walking into it.
 *
 * Deliberately asks whether anything *could* light rather than whether anything
 * currently does: an unlit Brazier is a light source waiting for a cast, and a
 * lamp on a dead line is the puzzle. Only a room with no candidate at all is
 * worth a warning. */
export function couldBeLit(room: RoomDef): boolean {
  return room.objects.some(
    (obj) =>
      (obj.heatState?.concept === 'energy' && Object.values(obj.heatState.states).some((s) => (s.holds ?? 0) > 0 || (s.bias ?? 0) > 0)) ||
      (obj.maxProperties?.energy ?? 0) > 0 ||
      obj.thermalContact?.concept === 'energy' ||
      obj.conduit?.feed !== undefined ||
      (obj.capacity ?? 0) > 0 ||
      obj.litTexture !== undefined,
  );
}
